/* WorkTrigGridViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const {ADDITIONAL_DATA_FIELDS} = require("./worktrigGridFields");
//const UserGridData = require('@gluenetworks/entity_grid_gl').UserData;
const Renderer = require("./WorkTrigGridViewRenderer");
const Registrations = require("../../../../../RegistrationNamespaces");
const DateColumn = require("./column_renderers/date_column/DateComponent");
// const ScheduleSolutionColumn = require('./column_renderers/schedule_solution/ScheduleSolutionComponent');
const ScheduleStatusColumn = require("./column_renderers/schedule_status/ScheduleStatusComponent");
const ScheduleTypeColumn = require("./column_renderers/schedule_type/ScheduleTypeComponent");
const lastOccurrenceStatusColumn = require("./column_renderers/last_occurrence_status/LastOccurrenceStatusComponent");

const PagingWorkTrigFeed = require("./PagingWorkTrigFeed");

const _ = require("lodash");
const {
    SCHEDULE_LAST_OCCURRENCE_STATUS,
    SCHEDULE_STATUSES,
    SCHEDULE_TYPES,
} = require("./scheduleConstants");

class WorkTrigGridViewComponent extends EventEmitter {
    constructor(
        logger,
        router,
        gameEditor,
        steveUtils,
        TriggerTypeEntity,
        userSession,
        solutionsManager,
        storageFactory
    ) {
        super();
        this._logger = logger;
        this._routeData = router.getRouteData() || {};
        this._gameEditor = gameEditor;
        this._steveUtils = steveUtils;
        this._TriggerTypeEntityDef = TriggerTypeEntity;
        this._userSession = userSession;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "scheduleExplorer";
        this._storageFactory = storageFactory;
        this._userGridData = undefined;
        this._pagingFeed = undefined;
        this._renderer = new Renderer(this);
        this._explorerState = {
            selectedIds: [],
        };
    }

    get columnConfig() {
        let columns = {
            solution: {
                label: "Solution",
                width: 120,
                priority: 10,
            },
            displayName: {
                label: "Name",
                width: 220,
                priority: 20,
            },
            description: {
                label: "Description",
                width: 300,
                priority: 30,
            },
            nextExecutionTime: {
                label: "Next Run",
                width: 160,
                columnRendererComponent: DateColumn,
                priority: 40,
                dataType: "date",
            },
            scheduleType: {
                label: "Schedule Type",
                path: "schedule.type",
                entityColumn: true,
                columnRendererComponent: ScheduleTypeColumn,
                width: 120,
                priority: 50,
                options: SCHEDULE_TYPES,
                dataType: "string",
            },
            status: {
                label: "Schedule Status",
                columnRendererComponent: ScheduleStatusColumn,
                width: 120,
                priority: 60,
                options: SCHEDULE_STATUSES,
            },
            lastOccurrenceStatus: {
                label: "Last Run Status",
                columnRendererComponent: lastOccurrenceStatusColumn,
                width: 120,
                priority: 60,
                options: SCHEDULE_LAST_OCCURRENCE_STATUS,
                dataType: "string",
            },
            createdOn: {
                label: "Created On",
                width: 160,
                priority: 70,
                columnRendererComponent: DateColumn,
                shown: false,
                dataType: "date",
                updateFromEntity: true,
            },
            createdBy: {
                label: "Created By",
                width: 120,
                priority: 80,
                shown: false,
                dataType: "string",
                updateFromEntity: true,
            },
            updatedOn: {
                label: "Updated On",
                width: 160,
                columnRendererComponent: DateColumn,
                priority: 90,
                shown: false,
                dataType: "date",
            },
            updatedBy: {
                label: "Updated By",
                width: 120,
                shown: false,
                dataType: "string",
            },
            lastExecutionTime: {
                label: "Last Activity On",
                width: 160,
                columnRendererComponent: DateColumn,
                priority: 110,
                dataType: "date",
            },
            // lastExecutionBy: {
            //     label: 'Last Activity By',
            //     priority: 120
            // }
        };

        return columns;
    }

    get pagingFeed() {
        return this._pagingFeed;
    }

    get selectedRows() {
        return this._renderer.selectedRows;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._gameEditor.on(
            "UpdateFilter",
            (this._onFilterChange = this._onFilterChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._gameEditor.removeListener("UpdateFilter", this._onFilterChange);
    }

    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._patchSorting(sort);

        const filter = this._pagingFeed.getFilterData();
        this._pagingFeed.setFilter(
            Object(filter).hasOwnProperty("$userQuery") ? filter.$userQuery : filter
        );
        this._pagingFeed.setSort(sort);
    }

    _patchSorting(sorting) {
        // Adding displayName as secondary sorting criteria
        if (sorting && Object.keys(sorting).length === 1) {
            if (this.columnConfig.displayName) {
                if (!sorting.displayName) {
                    sorting.displayName = 1;
                    sorting.id = 1;
                } else {
                    sorting.id = 1;
                }
            }
        }
    }

    _registerFeedListeners() {}

    _unregisterFeedListeners() {}

    _reestablishFeed() {
        this._pagingFeed.reestablish();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return this._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                const basicFilterQuery = _.get(
                    this._userGridData.getBasicSearchData(),
                    "filterQuery",
                    {}
                );
                const advancedFilterQuery = this._userGridData.getAdvancedSearchData();
                const userSorting = this._userGridData.getSorting() || {};
                this._patchSorting(userSorting);

                const filterQuery = !_.isEmpty(advancedFilterQuery)
                    ? advancedFilterQuery
                    : basicFilterQuery;

                this._pagingFeed = new PagingWorkTrigFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    entityDef: this._TriggerTypeEntityDef,
                    columnConfig: this.columnConfig,
                    filter: filterQuery,
                    sort: userSorting,
                    additionalDataFields: ADDITIONAL_DATA_FIELDS,
                });
                this._registerListeners();
            })
            .then(() => {
                return this._pagingFeed.whenFeedReady();
            });
    }

    wasFeedPreviouslyInitialized() {
        return this._pagingFeed.wasPreviouslyInitialized();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    updateFilter(filterString) {
        if (_.isEmpty(filterString)) {
            this._reestablishFeed();
        } else {
            this._reestablishFeed(filterString);
        }
    }

    shutdown() {
        this._unregisterFeedListeners();
        this._unregisterListeners();
        let shutdowns = [this._renderer.shutdown(), this._pagingFeed.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._routeData = undefined;
                this._TriggerTypeEntityDef = undefined;
                this._userSession = undefined;
                this._gridKey = undefined;
                this._userGridData = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._steveUtils = undefined;
                this._explorerState = undefined;
                this._pagingFeed = undefined;
                this._logger = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "WorkTrigGridViewComponent.shutdown() error");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get explorerState() {
        return this._explorerState;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    updateExplorerState(property, val) {
        _.set(this._explorerState, property, val);
    }

    resetSelection() {
        let selectedIds = this.getSelectedTriggerTypeIds().selectedRows;
        if (!selectedIds || !selectedIds.length) {
            return;
        }

        selectedIds.splice(0, selectedIds.length);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFilterChange(filterString) {
        this.updateExplorerState(filterString);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    openWorkTriggerInstanceEditor(id, fullyQualifiedViewName, launchOptions) {
        let triggerType = this._pagingFeed.getTriggerTypeEntityById(id);
        launchOptions = _.clone(launchOptions) || {};
        launchOptions.id = triggerType.id;
        launchOptions.triggerType = triggerType;
        launchOptions.title = triggerType.displayName;
        this._gameEditor.launchWorkTriggerEditor(
            {fqn: fullyQualifiedViewName || Registrations.WTE_VIEW_DETAILS},
            launchOptions
        );
    }

    getSelectedTriggerTypeIds() {
        if (!this._explorerState || !this._explorerState.selectedIds) {
            return [];
        }
        return this._explorerState.selectedIds;
    }

    getViewSettings() {
        if (this._routeData.downloadWorkResults) {
            return {
                view: Registrations.WTE_VIEW_HISTORY,
                action: "resultReport",
                triggerTypeId: this._routeData.downloadWorkResults.triggerTypeId,
                triggerId: this._routeData.downloadWorkResults.triggerId,
            };
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = WorkTrigGridViewComponent;

WorkTrigGridViewComponent.$inject = [
    "$teve.logger",
    "$teve.router",
    "gameEditor.gameEditor",
    "$teve.utils",
    "$teve.entities.TriggerType",
    "$teve.userSession",
    "$teve.solutionsManager",
    "$teve.storageFactory",
];

WorkTrigGridViewComponent.$displayDetails = {
    type: "View",
    name: "",
    icon: "configdrift.triggerType",
    shown: true,
};
